import type { Signal } from "@preact/signals";
import { useEffect } from "preact/hooks";
import type { Channel } from "~/web/src/channel";
import { AudioSheet } from "~/web/src/project/audio-sheet";
import { Files, SortBy } from "~/web/src/project/files";
import { ScriptEditor } from "~/web/src/project/script-editor";
import { SheetHead } from "~/web/src/project/sheet-head";
import { newest } from "~/web/src/project/top-pane";

// The right-hand sheet (mockup ux/06): opened only by the composer's buttons,
// closed by its ×, the scrim or Escape. Script = the newest .md, scene by
// scene (script-editor.tsx), Audio = the project's audio settings
// (audio-sheet.tsx), Files = the listing.

export type SheetMode = "script" | "audio" | "files";

const TITLE: Record<SheetMode, string> = { script: "Script", audio: "Audio", files: "Files" };

export function Sheet({
  channel,
  mode,
  viewing,
}: {
  channel: Channel;
  mode: Signal<SheetMode | null>;
  viewing: Signal<string | null>;
}) {
  const open = mode.value;
  const close = () => {
    mode.value = null;
  };
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);
  if (!open) return null;

  const script =
    open === "script" ? newest(channel.files.value, (f) => f.name.endsWith(".md")) : null;
  return (
    <>
      <button type="button" class="scrim" aria-label="Close" onClick={close} />
      <aside class="sheet" aria-label={TITLE[open]}>
        {open === "script" &&
          (script ? (
            <ScriptEditor key={script.name} channel={channel} name={script.name} onClose={close} />
          ) : (
            <>
              <SheetHead title={TITLE.script} onClose={close} />
              <div class="sb">
                <p class="dim">No script yet — the director writes one as it works.</p>
              </div>
            </>
          ))}
        {open === "audio" && <AudioSheet channel={channel} onClose={close} />}
        {open === "files" && (
          <>
            <SheetHead title={TITLE.files} onClose={close}>
              <SortBy />
            </SheetHead>
            <Files
              channel={channel}
              onPick={(name) => {
                viewing.value = name;
                close();
              }}
            />
          </>
        )}
      </aside>
    </>
  );
}
